
import { defineComponent, ref, computed, onMounted, defineAsyncComponent } from "vue";
import { useRouter } from "vue-router";
import ApiService from "@/core/services/ApiService";
import { Dashboard, criticityColor, entityQueryString } from "./trf";

// Ranked-list card and world map, loaded as async components.
const TopList = defineAsyncComponent(() => import("./TopList.vue"));
const CountryMap = defineAsyncComponent(() => import("./CountryMap.vue"));

export default defineComponent({
  name: "quimerax-dashboard",
  components: { TopList, CountryMap },
  setup() {
    const router = useRouter();
    const data = ref<Dashboard | null>(null);
    const isLoading = ref(false);
    const window = ref(90);
    const windows = [30, 90, 180, 366];

    const GOLD = "#fbba00";

    const fetchDashboard = async () => {
      isLoading.value = true;
      try {
        const res = await ApiService.get(`/quimerax-news/dashboard?window=${window.value}`);
        data.value = res.data?.challenge_required ? null : res.data;
      } catch (e) {
        data.value = null;
      } finally {
        isLoading.value = false;
      }
    };

    const setWindow = (w: number) => {
      window.value = w;
      fetchDashboard();
    };

    // Navigate to the feed with a filter clause.
    const filter = (field: string, value: string, quoted = true) => {
      const clause = quoted ? entityQueryString(field, value) : `${field}:${value}`;
      router.push({ path: "/apps/quimerax-news", query: { q: clause } });
    };

    const criticityTiles = computed(() => {
      const order = ["critical", "high", "medium", "low"];
      const map = new Map((data.value?.criticity ?? []).map((b) => [b.key, b.count]));
      return order
        .filter((k) => map.has(k))
        .map((k) => ({ key: k, count: map.get(k) ?? 0, color: criticityColor(k) }));
    });

    // --- Activity area chart ---
    const activitySeries = computed(() => [
      {
        name: "Casos",
        data: (data.value?.activity ?? []).map((b) => ({ x: b.key, y: b.count })),
      },
    ]);
    const activityOptions = {
      chart: { toolbar: { show: false }, zoom: { enabled: false }, sparkline: { enabled: false } },
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 2, colors: [GOLD] },
      fill: {
        type: "gradient",
        gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 90, 100] },
        colors: [GOLD],
      },
      colors: [GOLD],
      xaxis: {
        type: "datetime",
        labels: { style: { colors: "#a1a5b7" } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { style: { colors: "#a1a5b7" } } },
      grid: { borderColor: "rgba(255,255,255,0.06)", strokeDashArray: 4 },
      tooltip: { theme: "dark", x: { format: "dd MMM yyyy" } },
    };

    onMounted(fetchDashboard);

    return {
      data,
      isLoading,
      window,
      windows,
      setWindow,
      filter,
      criticityTiles,
      activitySeries,
      activityOptions,
    };
  },
});
